import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { QuillModule } from 'ngx-quill';
import { Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { HtmlFormatterComponent } from 'src/app/components/data-table/html-formatter/html-formatter.component';
import { FormButtonComponent } from 'src/app/components/dynamic-form/form-button/form-button.component';
import { IconModule } from 'src/app/icon/icon.module';
import { MaterialModule } from 'src/app/material.module';
import { InformationService } from 'src/app/services/information.service';
import { LoadingService } from 'src/app/services/loading.service';
import { ToastService } from 'src/app/services/toast.service';
import { hasEulaContent } from '../../eula/eula-content';
import { EulaQuillHost } from '../../eula/eula-quill-host';
import { EULA_QUILL_MODULES } from '../../eula/eula-quill.config';
import { TableService } from 'src/app/services/table.service';
import { IFormActions } from 'src/app/shared/interfaces/IForm';
import { formatDateToLocalTimeZone } from 'src/app/shared/utils';

@Component({
  selector: 'app-information-detail',
  standalone: true,
  imports: [MaterialModule, IconModule, TranslateModule, ReactiveFormsModule, QuillModule, HtmlFormatterComponent, FormButtonComponent],
  templateUrl: './information-detail.component.html',
  styleUrl: './information-detail.component.scss',
  providers: [EulaQuillHost]
})
export class InformationDetailComponent implements OnInit, OnDestroy {
  readonly quillModules = EULA_QUILL_MODULES;
  informationForm: FormGroup;
  informationId = 0;
  spinLoader = false;
  langChangeSub?: Subscription;
  isAdmin = false;
  information: any = {};
  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private tableService: TableService,
    private toastService: ToastService,
    private loadingService: LoadingService,
    private informationService: InformationService,
    private editor: EulaQuillHost) {
      this.informationId = Number(this.route.snapshot.params['informationId']) || 0;
      this.isAdmin = this.router.url.startsWith('/admin');
    }

  ngOnInit() {
    this.initForm();
    if (this.informationId) this.getInformation();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => this.editor.updateTooltips());
  }

  ngOnDestroy() {
    this.langChangeSub?.unsubscribe();
    this.editor.destroy();
  }

  get subjectControl() {
    return this.informationForm.get('subject')!;
  }

  get messageControl() {
    return this.informationForm.get('message')!;
  }

  onEditorCreated(quill: any) {
    this.editor.attach(quill);
  }

  onPreviewClick(event: MouseEvent) {
    this.editor.onPreviewClick(event);
  }

  get saveButtonConfig(): IFormActions {
    return {
      text: 'save',
      type: 'submit'
    };
  }

  save() {
    this.informationForm.markAllAsTouched();
    if (this.informationForm.invalid) return;

    this.spinLoader = true;
    this.informationService.createOrUpdateInformation({
      ...this.informationForm.value,
      message: this.editor.persist(this.messageControl.value || '')
    }).subscribe({
      next: (id) => {
        this.informationForm.patchValue({ id });
        this.informationId = id;
        this.spinLoader = false;
        this.toastService.success(this.translate.instant('update successfully'));
      },
      error: ({ error }) => {
        this.spinLoader = false;
        this.toastService.error(this.translate.instant(error?.Message || 'error'));
      }
    });
  }

  close() {
    this.tableService.markForRestore('information-table');
    this.router.navigate([`${this.isAdmin ? '/admin' : ''}/information`]);
  }

  private initForm() {
    this.informationForm = this.formBuilder.group({
      id: [this.informationId],
      subject: ['', [Validators.required, Validators.maxLength(100)]],
      message: ['', [this.messageValidator]]
    });
  }

  private getInformation() {
    this.loadingService.show();
    this.informationService.getInformation(this.informationId).pipe(
      switchMap((information) => {
        if (!information) throw { status: 404 };
        return this.editor.hydrate(information.message).pipe(
          map((message) => {
            this.information = information;
            this.information.createdDate = formatDateToLocalTimeZone(information.createdDate);
            return { ...information, message };
          })
        );
      })
    ).subscribe({
      next: (information) => {
        this.informationForm.patchValue(information);
        this.loadingService.hide();
      },
      error: () => {
        this.loadingService.hide();
        this.toastService.error(this.translate.instant('error'));
        this.close();
      }
    });
  }

  private messageValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
    hasEulaContent(control.value) ? null : { required: true };
}
